<template>
  <div :class="computedClass">
    <div class="el-calendar__header">
      <div
        v-if="validatedRange.length === 0"
        class="el-calendar__button-group"
      >
        <slot name="header">
          <slot name="headerLeft"></slot>
          <rt-picklist
            ref="yearPicklist"
            v-model="yearValue"
            :fetch="fetchYear"
            :clearable="false"
            @change="handleChangeYear"
          />
          <rt-picklist
            v-model="monthValue"
            :fetch="fetchMonth"
            :clearable="false"
            @change="handleChangeMonth"
          />
          <rt-button
            type="default"
            @click="selectDate('today')"
          >
            {{ t('el.datepicker.today') }}
          </rt-button>
          <slot name="headerRight"></slot>
        </slot>
      </div>
    </div>
    <div
      v-if="validatedRange.length === 0"
      key="no-range"
      class="el-calendar__body"
    >
      <date-table
        :date="date"
        :selected-day="realSelectedDay"
        :first-day-of-week="realFirstDayOfWeek"
        @pick="pickDay"
      />
    </div>
    <div
      v-else
      key="has-range"
      class="el-calendar__body"
    >
      <date-table
        v-for="(item, index) in validatedRange"
        :key="index"
        :date="item[0]"
        :selected-day="realSelectedDay"
        :range="item"
        :hide-header="index !== 0"
        :first-day-of-week="realFirstDayOfWeek"
        @pick="pickDay"
      />
    </div>
    <div class="rt-calendar-footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<script>
import Locale from 'element-ui/lib/mixins/locale';
import fecha from 'element-ui/lib/utils/date';
import DateTable from './date-table';
import RtButton from 'vue-xpc/src/components/button/rtButton.vue';
import RtPicklist from 'vue-xpc/src/components/picklist/index.vue';
import { validateRangeInOneMonth } from 'element-ui/lib/utils/date-util';

const validTypes = ['prev-month', 'today', 'next-month'];
const weekDays = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
];
const oneDay = 86400000;

export default {
  name: 'ElCalendar',

  components: {
    DateTable,
    RtButton,
    RtPicklist
  },

  mixins: [Locale],

  props: {
    value: [Date, String, Number],
    range: {
      type: Array,
      validator (range) {
        if (Array.isArray(range)) {
          return (
            range.length === 2 &&
            range.every(
              (item) =>
                typeof item === 'string' ||
                typeof item === 'number' ||
                item instanceof Date
            )
          );
        } else {
          return true;
        }
      }
    },
    firstDayOfWeek: {
      type: Number,
      default: 7
    },
    /**
     * @public
     * 支持切换风格，默认 full，可选 card
     */
    theme: {
      type: String,
      default: 'full'
    }
  },

  provide () {
    return {
      elCalendar: this
    };
  },

  data () {
    return {
      selectedDay: '',
      now: new Date(),
      yearOptions: [],
      monthOptions: [],
      yearValue: '',
      monthValue: ''
    };
  },

  computed: {
    computedClass () {
      const { theme } = this;
      let classList = ['el-calendar', 'rt-calendar'];

      if (theme) {
        classList.push(`rt-calendar-${theme}`)
      }

      return classList;
    },

    prevMonthDatePrefix () {
      const temp = new Date(this.date.getTime());
      temp.setDate(0);
      return fecha.format(temp, 'yyyy-MM');
    },

    curMonthDatePrefix () {
      return fecha.format(this.date, 'yyyy-MM');
    },

    nextMonthDatePrefix () {
      const temp = new Date(
        this.date.getFullYear(),
        this.date.getMonth() + 1,
        1
      );
      return fecha.format(temp, 'yyyy-MM');
    },

    formattedDate () {
      return fecha.format(this.date, 'yyyy-MM-dd');
    },

    formattedToday () {
      return fecha.format(this.now, 'yyyy-MM-dd');
    },

    realSelectedDay: {
      get () {
        if (!this.value) return this.selectedDay;
        return this.formattedDate;
      },
      set (val) {
        this.selectedDay = val;
        const date = new Date(val);
        this.$emit('input', date);
      }
    },

    date () {
      if (!this.value) {
        if (this.realSelectedDay) {
          const d = this.selectedDay.split('-');
          return new Date(d[0], d[1] - 1, d[2]);
        } else if (this.validatedRange.length) {
          return this.validatedRange[0][0];
        }
        return this.now;
      } else {
        return this.toDate(this.value);
      }
    },

    computedYearValue () {
      return Number(fecha.format(this.date, 'yyyy'));
    },

    computedMonthValue () {
      return Number(fecha.format(this.date, 'MM')) - 1;
    },

    // if range is valid, we get a two-digit array
    validatedRange () {
      let range = this.range;
      if (!range) return [];
      range = range.reduce((prev, val, index) => {
        const date = this.toDate(val);
        if (this.rangeValidator(date, index === 0)) {
          prev = prev.concat(date);
        }
        return prev;
      }, []);
      if (range.length === 2) {
        const [start, end] = range;
        if (start > end) {
          console.warn(
            '[ElementCalendar]end time should be greater than start time'
          );
          return [];
        }
        // start time and end time in one month
        if (validateRangeInOneMonth(start, end)) {
          return [[start, end]];
        }
        const data = [];
        let startDay = new Date(start.getFullYear(), start.getMonth() + 1, 1);
        const lastDay = this.toDate(startDay.getTime() - oneDay);
        if (!validateRangeInOneMonth(startDay, end)) {
          console.warn(
            '[ElementCalendar]start time and end time interval must not exceed two months'
          );
          return [];
        }
        // 第一个月的时间范围
        data.push([start, lastDay]);
        // 下一月的时间范围，需要计算一下该月的第一个周起始日
        const firstDayOfWeek = this.realFirstDayOfWeek;
        const nextMontFirstDay = startDay.getDay();
        let interval = 0;
        if (nextMontFirstDay !== firstDayOfWeek) {
          if (firstDayOfWeek === 0) {
            interval = 7 - nextMontFirstDay;
          } else {
            interval = firstDayOfWeek - nextMontFirstDay;
            interval = interval > 0 ? interval : 7 + interval;
          }
        }
        startDay = this.toDate(startDay.getTime() + interval * oneDay);
        if (startDay.getDate() < end.getDate()) {
          data.push([startDay, end]);
        }
        return data;
      }
      return [];
    },

    realFirstDayOfWeek () {
      if (this.firstDayOfWeek < 1 || this.firstDayOfWeek > 6) {
        return 0;
      }
      return Math.floor(this.firstDayOfWeek);
    }
  },

  watch: {
    value (val) {
      this.yearValue = val.getFullYear();
      this.monthValue = val.getMonth();
    }
  },

  mounted () {
    this.updateYearOptions();
    this.updateMonthOptions();
    this.yearValue = Number(fecha.format(this.date, 'yyyy'));
    this.monthValue = Number(fecha.format(this.date, 'MM')) - 1;
  },

  methods: {
    handleChangeYear () {
      const month = this.date.getMonth();
      let value = new Date(this.yearValue, month)
      this.pickDay(fecha.format(value, 'yyyy-MM-dd'));
      this.yearOptions = [];
      this.updateYearOptions(this.yearValue);
    },
    handleChangeMonth () {
      const year = this.date.getFullYear();
      let value = new Date(year, this.monthValue)
      this.pickDay(fecha.format(value, 'yyyy-MM-dd'));
    },
    updateYearOptions (val) {
      let currentYear = val || this.now.getFullYear();
      for (let i = currentYear - 10; i < currentYear + 10; i++) {
        this.yearOptions.push({
          Text: `${i} ${this.t('el.datepicker.year')}`,
          Value: i
        });
      }
      this.$refs.yearPicklist && this.$refs.yearPicklist.bindPicklist();
    },
    updateMonthOptions () {
      for (let i = 0; i <= 11; i++) {
        this.monthOptions.push({
          Text: this.t(`el.datepicker.month${i + 1}`),
          Value: i
        })
      }
    },
    fetchMonth () {
      return new Promise(resolve => {
        resolve(this.monthOptions);
      });
    },
    fetchYear () {
      return new Promise(resolve => {
        resolve(this.yearOptions);
      });
    },
    pickDay (day) {
      this.realSelectedDay = day;
      this.$emit('select', day)
    },

    selectDate (type) {
      if (validTypes.indexOf(type) === -1) {
        throw new Error(`invalid type ${type}`);
      }
      let day = '';
      if (type === 'prev-month') {
        day = `${this.prevMonthDatePrefix}-01`;
      } else if (type === 'next-month') {
        day = `${this.nextMonthDatePrefix}-01`;
      } else {
        day = this.formattedToday;
      }

      if (day === this.formattedDate) return;
      this.pickDay(day);
    },

    toDate (val) {
      if (!val) {
        throw new Error('invalid val');
      }
      return val instanceof Date ? val : new Date(val);
    },

    rangeValidator (date, isStart) {
      const firstDayOfWeek = this.realFirstDayOfWeek;
      const expected = isStart
        ? firstDayOfWeek
        : firstDayOfWeek === 0
          ? 6
          : firstDayOfWeek - 1;
      const message = `${isStart ? 'start' : 'end'} of range should be ${
        weekDays[expected]
      }.`;
      if (date.getDay() !== expected) {
        console.warn(
          '[ElementCalendar]',
          message,
          'Invalid range will be ignored.'
        );
        return false;
      }
      return true;
    }
  }
};
</script>



// WEBPACK FOOTER //
// main.vue?62c197bd